import { Link } from 'react-router-dom';
import { TooltipOnTruncated } from '@components/TooltipOnTruncated';
import type { UISelectionType } from '@components/types';
import { Badge } from '@components/ui/badge';
import type { DbTag } from '@domain/schemas/tag.schema';
import type { Team } from '@domain/schemas/team.schema';
import type { EntityData } from '@domain/types/entity';
import { isNonNullish } from '@root/helpers';
import type { CellContext } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { FileIcon, RotateCw } from 'lucide-react';

import { contractValidAccessor } from './submissions/contractValidAccessor';
import {
  classOfBusinessAccessor,
  createdAtAccessor,
  createdByAccessor,
  lastUpdatedByAccessor,
  tagsAccessor,
  teamsAccessor,
  updatedAtAccessor,
} from './shared';
import {
  brandingAccessor,
  commentsAccessor,
  endorsementsAccessor,
  getBaseAccessor,
  guidanceAccessor,
  renewalsAccessor,
  sourceResourceAccessor,
  statusAccessor,
} from './submissions';

type Submission = EntityData & { created_by: string; last_updated_by?: string };

const columnHelper = createColumnHelper<Submission>();

interface GetSubmissionTableColumnsOptions {
  brandingFieldName: 'branding' | 'branding_id';
  brandingOptions: UISelectionType[] | string[];
  createdByUsers?: string[];
  disableStatus?: boolean;
  hasCommentsFeature: boolean;
  hasEndorsementsFeature: boolean;
  hasFirmOrderStatus: boolean;
  hasPreDrafts?: boolean;
  /** Contract Guidance preview access (PbqaEval flag + either preview cohort). */
  hasGuidanceFeature?: boolean;
  isAdmin?: boolean;
  tags: DbTag[];
  teams: Team[];
  templates?: string[];
  updatedByUsers?: string[];
}

export const getSubmissionsTableColumns = ({
  brandingFieldName,
  brandingOptions,
  isAdmin = false,
  disableStatus = false,
  hasCommentsFeature,
  hasEndorsementsFeature,
  hasFirmOrderStatus,
  hasPreDrafts = false,
  hasGuidanceFeature = false,
  tags,
  teams,
  templates,
  createdByUsers,
  updatedByUsers,
}: GetSubmissionTableColumnsOptions) => {
  return [
    columnHelper.accessor('id', {
      id: 'id',
      enableColumnFilter: false,
      enableHiding: false,
    }),
    columnHelper.accessor('ref', {
      id: 'ref',
      enableColumnFilter: false,
      header: 'Contract ID',
      filterFn: 'arrIncludesSome',
      meta: {
        filter: {
          placeholder: 'All IDs',
        },

        dataCellClassName: 'truncate',
        headingCellClassName: 'truncate',
      },
      sortingFn: 'text',
      minSize: 144,
      size: 144,
      maxSize: 144,
    }),
    columnHelper.accessor('name', {
      id: 'name',
      enableColumnFilter: false,
      header: 'Contract Name',
      sortingFn: 'text',
      sortUndefined: -1,
      enableHiding: false,
      filterFn: 'arrIncludesSome',
      size: 300,
      meta: {
        filter: {
          label: 'Contract name',
          placeholder: 'All Contracts',
        },
        headingCellClassName: 'grow truncate',
        dataCellClassName: 'grow !max-w-full',
      },
      cell: (cellContext: CellContext<Submission, string>) => (
        <TooltipOnTruncated tooltipContent={cellContext.getValue()}>
          <span className="truncate">{cellContext.getValue()}</span>
        </TooltipOnTruncated>
      ),
    }),
    columnHelper.accessor('external_id', {
      id: 'external_id',
      enableColumnFilter: false,
      header: 'External ID',
      sortingFn: 'text',
      sortUndefined: -1,
      filterFn: 'arrIncludesSome',
      size: 300,
      meta: {
        filter: {
          label: 'External ID',
          placeholder: 'All Contracts',
        },
        headingCellClassName: 'grow truncate',
        dataCellClassName: 'grow !max-w-full',
      },
      cell: (cellContext: CellContext<Submission, string>) => (
        <TooltipOnTruncated tooltipContent={cellContext.getValue()}>
          <span className="truncate">{cellContext.getValue()}</span>
        </TooltipOnTruncated>
      ),
    }),
    columnHelper.accessor('umr', {
      id: 'umr',
      enableColumnFilter: false,
      header: 'UMR',
      sortingFn: 'text',
      sortUndefined: -1,
      filterFn: 'arrIncludesSome',
      size: 164,
      meta: {
        tooltip: true,
        filter: {
          placeholder: 'All UMRs',
        },
        headingCellClassName: 'truncate',
        dataCellClassName: 'truncate',
      },
    }),
    teamsAccessor<Submission>(columnHelper, teams, {}, 'Team'),
    disableStatus ? null : statusAccessor<Submission>(columnHelper, hasFirmOrderStatus, hasPreDrafts),
    contractValidAccessor<Submission>(columnHelper),
    hasCommentsFeature ? commentsAccessor<Submission>(columnHelper) : null,
    hasEndorsementsFeature ? endorsementsAccessor<Submission>(columnHelper) : null,
    hasEndorsementsFeature ? renewalsAccessor<Submission>(columnHelper) : null,
    columnHelper.accessor((entity) => entity.renewalParentId, {
      id: 'renewalParentId',
      header: 'Type',
      cell: (cellContext: CellContext<Submission, string>) => {
        const isRenewal = !!cellContext.getValue();
        return (
          <Badge variant="secondary">
            {isRenewal ? <RotateCw data-icon="inline-start" /> : <FileIcon data-icon="inline-start" />}
            {isRenewal ? 'Renewal' : 'Original'}
          </Badge>
        );
      },
    }),
    hasGuidanceFeature ? guidanceAccessor<Submission>(columnHelper) : null,
    tagsAccessor<Submission>(columnHelper, tags),
    lastUpdatedByAccessor<Submission>(columnHelper, {}, updatedByUsers),
    updatedAtAccessor(columnHelper),
    createdByAccessor(columnHelper, createdByUsers),
    createdAtAccessor(columnHelper),
    brandingAccessor<Submission>(columnHelper, brandingFieldName, brandingOptions),
    classOfBusinessAccessor<Submission>(columnHelper),
    sourceResourceAccessor<Submission>(columnHelper, templates, isAdmin),
    getBaseAccessor<Submission>(columnHelper),
    columnHelper.display({
      id: 'view',
      header: '',
      enableHiding: false,
      size: 65,
      minSize: 65,
      maxSize: 65,
      enableColumnFilter: false,
      cell: (cellContext: CellContext<Submission, unknown>) => (
        <Link
          className="text-primary-600 hover:text-primary-900 inline-flex w-full items-center justify-center font-light"
          target="_self"
          to={`/contracts/${cellContext.row.getValue('id')}/contract`}
          rel="noreferrer"
        >
          View
        </Link>
      ),
      meta: {
        dataCellClassName: 'relative truncate whitespace-nowrap px-1 py-0 text-center text-sm font-medium',
      },
    }),
  ].filter(isNonNullish);
};
